import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getErrorInfo, scrollToError, generateErrorId, ErrorInfo } from '@/lib/errorUtils';

interface UseErrorHandlerOptions {
  showToast?: boolean;
  scrollToError?: boolean;
  autoFocus?: boolean;
  onError?: (error: any, errorInfo: ErrorInfo) => void;
}

interface ErrorState {
  error: ErrorInfo | null;
  errorId: string | null;
  isError: boolean;
}

export const useErrorHandler = (options: UseErrorHandlerOptions = {}) => {
  const {
    showToast = true,
    scrollToError: shouldScrollToError = true,
    autoFocus = true,
    onError
  } = options;

  const { toast } = useToast();
  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    errorId: null,
    isError: false
  });

  const handleError = useCallback((error: any, customMessage?: string) => {
    const errorInfo = customMessage
      ? { ...getErrorInfo(error), message: customMessage }
      : getErrorInfo(error);

    const errorId = generateErrorId();

    // Update error state
    setErrorState({
      error: errorInfo,
      errorId,
      isError: true
    });

    // Show toast notification if enabled
    if (showToast) {
      toast({
        title: errorInfo.title,
        description: errorInfo.message,
        variant: 'destructive',
        // duration: errorInfo.retryable ? 8000 : 5000
      });
    }

    // Scroll to error if enabled
    if (shouldScrollToError) {
      // Small delay to ensure the error element is rendered
      setTimeout(() => {
        scrollToError(errorId);
      }, 100);
    }

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Log error for debugging
    console.error('Error handled:', {
      originalError: error,
      errorInfo,
      errorId
    });

    return errorInfo;
  }, [showToast, shouldScrollToError, autoFocus, onError, toast]);

  const clearError = useCallback(() => {
    setErrorState({
      error: null,
      errorId: null,
      isError: false
    });
  }, []);

  const retryWithErrorHandling = useCallback(async (
    asyncFunction: () => Promise<any>,
    customErrorMessage?: string
  ) => {
    try {
      clearError();
      const result = await asyncFunction();
      return result;
    } catch (error) {
      handleError(error, customErrorMessage);
      throw error; // Re-throw so calling code can handle it if needed
    }
  }, [handleError, clearError]);

  return {
    // Error state
    error: errorState.error,
    errorId: errorState.errorId,
    isError: errorState.isError,

    // Error handling functions
    handleError,
    clearError,
    retryWithErrorHandling,

    // Utility functions
    getErrorInfo: (error: any) => getErrorInfo(error),
    scrollToError: (elementId?: string) => scrollToError(elementId)
  };
};

export default useErrorHandler;
